import React, { useState } from "react"
import { UnstyledButton } from "@mantine/core"

import { useStyles } from "@/pages/campaign-form/components/core/CampaignTypeSelect/CampaignTypeSelect.style"
import { CampaignTypeCard } from "@/pages/campaign-form/components/core/CampaignTypeSelect/CampaignTypeCard/CampaignTypeCard"
import { Icon } from "@/components"
import { listCampaignTypeItemsForNewCampaignForm } from "@/services/campaigns"
import { ANIMATION_DURATION } from "@/pages/campaign-form/components/core/CampaignTypeSelect/CampaignTypeSelect.constants"
import { CampaignType } from "@/types/djangio"
import { isFeatureEnabled } from "shared/featureflags/helperFunctions"

interface CampaignTypeSelectProps {
    selectedCampaignType: string
    setSelectedCampaignType: (value: string | null) => void
}

const campaignTypes = listCampaignTypeItemsForNewCampaignForm()
    .sort((a, b) => a.order - b.order)
    //remove this filter when remediating ff_ngg_personalized_messages_campaign
    .filter((campaignType) => {
        if (!isFeatureEnabled("ff_ngg_personalized_messages_campaign")) {
            return campaignType.value !== DjangIO.app.grassroots.campaign.types.CampaignType.personalized_messages.value
        }
        return true
    })

const mostUsedCampaigns = campaignTypes.slice(0, 8)

const isOneOfMostUsedCampaigns = (selectedCampaignType: string) =>
    mostUsedCampaigns.map((campaignType) => campaignType.key).includes(selectedCampaignType)

export const CampaignTypeSelect = ({ selectedCampaignType, setSelectedCampaignType }: CampaignTypeSelectProps) => {
    const [shouldShowMoreOptions, setShouldShowMoreOptions] = useState(false)
    const [campaignOptions, setCampaignOptions] = useState<typeof campaignTypes>(mostUsedCampaigns)

    const { classes } = useStyles({ isExpanded: shouldShowMoreOptions })

    const handleClickMoreOptions = () => {
        setShouldShowMoreOptions((lastShouldShowMoreOptions) => {
            // wait till animation finishes to clean the extra select option
            setTimeout(() => {
                if (!isOneOfMostUsedCampaigns(selectedCampaignType) && lastShouldShowMoreOptions) {
                    setSelectedCampaignType(null)
                }
                if (lastShouldShowMoreOptions) {
                    setCampaignOptions(lastShouldShowMoreOptions ? mostUsedCampaigns : campaignTypes)
                }
            }, ANIMATION_DURATION)
            if (!lastShouldShowMoreOptions) setCampaignOptions(campaignTypes)

            return !lastShouldShowMoreOptions
        })
    }

    const isCampaignNew = (campaignType: CampaignType) => {
        const isShareStory =
            campaignType.value === DjangIO.app.grassroots.campaign.types.CampaignType.grassroots_story.value
        const isPersonalizedMessages =
            campaignType.value === DjangIO.app.grassroots.campaign.types.CampaignType.personalized_messages.value
        const showPersonalizedCampaignNew =
            isPersonalizedMessages && isFeatureEnabled("ff_ngg_personalized_messages_campaign")

        return isShareStory || showPersonalizedCampaignNew
    }

    const getDescriptionForCampaignType = (campaignType: CampaignType) => {
        const isShareStory =
            campaignType.value === DjangIO.app.grassroots.campaign.types.CampaignType.grassroots_story.value
        if (isShareStory) {
            return "Grassroots contacts share stories with you about experiences they had."
        }
        return campaignType.minimal_tip
    }

    return (
        <>
            <div className={classes.frame}>
                {campaignOptions.map((campaignType) => (
                    <CampaignTypeCard
                        isNew={isCampaignNew(campaignType)}
                        title={campaignType.label}
                        icon={campaignType.new_icon}
                        iconFamily={campaignType.icon_family}
                        description={getDescriptionForCampaignType(campaignType)}
                        name={campaignType.key}
                        key={campaignType.key}
                        campaignType={selectedCampaignType}
                        setCampaignType={setSelectedCampaignType}
                    />
                ))}
            </div>
            <UnstyledButton className={classes.moreOptionsButton} onClick={handleClickMoreOptions}>
                {shouldShowMoreOptions ? "Less" : "More"} options
                <Icon className={classes.moreOptionsIcon} icon="angle-down" />
            </UnstyledButton>
        </>
    )
}
